const KINDLY_EVENT = 'kindly:load';
const KINDLY_BUBBLE_SELECTOR = '[data-testid="chat-bubble-button"]';
export const ZUZANA_CONTAINER_ID = 'zuzana-chat-container';
const ZUZANA_BUBBLE_SELECTOR = '.bottom-6';

let chatbot: HTMLElement | null = null;

// Called whenever chatbot is set so a pre-visible bar is nudged immediately
let onChatbotReady: (() => void) | null = null;

// Kindly fires kindly:load on its host element. Registered at module scope so
// it's never missed regardless of when PostHog resolves and initStickyAtcBar runs.
window.addEventListener(
  KINDLY_EVENT,
  (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    // #kindly-chat-api is position:relative in normal flow — its first child
    // is the shadow host that holds the actual fixed-position widget
    const inner = event.target.firstElementChild;
    chatbot = inner instanceof HTMLElement ? inner : event.target;
    onChatbotReady?.();
  },
  { once: true }
);

function trackZuzanaEl(): void {
  // Zuzana appends its container to <body> asynchronously after page load.
  // If it's already there (e.g. fast connection), grab it directly; otherwise
  // observe <body> and pick it up as soon as it's added.
  const existing = document.getElementById(ZUZANA_CONTAINER_ID);
  if (existing) {
    chatbot = existing;
    return;
  }
  const observer = new MutationObserver((_, obs) => {
    const zuzanaEl = document.getElementById(ZUZANA_CONTAINER_ID);
    if (!zuzanaEl) return;
    obs.disconnect();
    chatbot = zuzanaEl;
    onChatbotReady?.();
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

export function initChatbotNudge(mountId: string): void {
  trackZuzanaEl();

  const container = document.getElementById(mountId);
  if (!container) return;

  // The specific button inside the chatbot's shadow DOM.
  let chatbotBubble: HTMLElement | null = null;

  // Watch for the sticky ATC bar div being added/removed inside its mount point.
  // Vue's v-if inserts/removes it, so each change means the bar slid in or out.
  function syncChatbotPosition(): void {
    if (!chatbot) return;
    const atcBar = container!.firstElementChild as HTMLElement | null;

    if (!chatbotBubble) {
      // Both chatbots use shadow DOM. We target the chat bubble button directly
      // so we can move it independently of its container.
      // Kindly exposes a stable data-testid; Zuzana uses a bottom-6 Tailwind class.
      chatbotBubble =
        chatbot.shadowRoot?.querySelector<HTMLElement>(
          KINDLY_BUBBLE_SELECTOR
        ) ??
        chatbot.shadowRoot?.querySelector<HTMLElement>(
          ZUZANA_BUBBLE_SELECTOR
        ) ??
        null;
      if (chatbotBubble)
        chatbotBubble.style.transition =
          'transform 0.5s ease, bottom 0.5s ease';
    }

    const isZuzana = chatbot?.id === ZUZANA_CONTAINER_ID;

    if (atcBar) {
      // Nudge the button up by the bar's height + a small gap so it's never covered
      if (isZuzana) chatbotBubble?.style.setProperty('bottom', '5px');
      chatbotBubble?.style.setProperty(
        'transform',
        `translateY(-${atcBar.offsetHeight}px)`
      );
    } else {
      // Bar gone — let the button's own CSS take back control
      if (isZuzana) chatbotBubble?.style.removeProperty('bottom');
      chatbotBubble?.style.removeProperty('transform');
    }
  }

  // Expose sync so the chatbot trackers can trigger it once chatbot is ready
  onChatbotReady = syncChatbotPosition;

  new MutationObserver(syncChatbotPosition).observe(container, {
    childList: true
  });

  // Handle the case where the bar is already visible on first load
  // (user refreshed mid-scroll or navigated to a pre-scrolled anchor)
  syncChatbotPosition();
}
